import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import api from "../../lib/api";
import PropertyCard from "../PropertyCard";
import { Reveal } from "../Reveal";

const FILTERS = [
  { key: "all", label: "Tous" },
  { key: "type:Villa|Maison", label: "Villas & Maisons" },
  { key: "type:Appartement", label: "Appartements" },
  { key: "tag:Exclusivité", label: "Exclusivités" },
  { key: "type:Local commercial", label: "Locaux commerciaux" },
  { key: "tag:Investissement", label: "Investissement" },
];

export default function Properties() {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [filter, setFilter] = useState("all");
  const trackRef = useRef(null);

  useEffect(() => {
    api.get("/properties").then((r) => setProperties(r.data)).catch(() => {});
  }, []);

  const visible = properties.filter((p) => {
    if (filter === "all") return true;
    const [kind, value] = filter.split(":");
    return kind === "type" ? value.split("|").includes(p.type) : p.tags?.includes(value);
  });

  const scroll = (dir) => {
    trackRef.current?.scrollBy({ left: dir * 460, behavior: "smooth" });
  };

  return (
    <section id="nos-biens" data-testid="properties-section" className="overflow-hidden pb-4 pt-6 lg:pt-10">
      <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 sm:px-8 lg:px-16">
        <Reveal delay={0.1} className="no-scrollbar flex flex-1 flex-nowrap gap-1.5 overflow-x-auto sm:gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`filter-${f.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className={`relative shrink-0 whitespace-nowrap rounded-full px-3 py-2 font-serif text-[11px] font-medium uppercase tracking-[0.1em] transition-colors duration-300 sm:px-5 sm:py-2.5 sm:text-base sm:tracking-[0.14em] ${
                filter === f.key ? "text-sand" : "text-azur hover:text-gold"
              }`}
            >
              {filter === f.key && (
                <motion.span
                  layoutId="filter-pill"
                  className="absolute inset-0 rounded-full bg-azur"
                  transition={{ type: "spring", stiffness: 350, damping: 30 }}
                />
              )}
              <span className="relative">{f.label}</span>
            </button>
          ))}
        </Reveal>
        <div className="hidden shrink-0 items-center gap-2 sm:flex">
          <button
            onClick={() => scroll(-1)}
            data-testid="carousel-prev"
            aria-label="Précédent"
            className="rounded-full border border-slate-300 p-2.5 text-azur transition-all duration-300 hover:border-azur hover:bg-azur hover:text-sand"
          >
            <ArrowLeft size={16} />
          </button>
          <button
            onClick={() => scroll(1)}
            data-testid="carousel-next"
            aria-label="Suivant"
            className="rounded-full border border-slate-300 p-2.5 text-azur transition-all duration-300 hover:border-azur hover:bg-azur hover:text-sand"
          >
            <ArrowRight size={16} />
          </button>
        </div>
      </div>

      <div
        ref={trackRef}
        data-testid="properties-carousel"
        className="no-scrollbar mt-6 flex snap-x snap-mandatory gap-6 overflow-x-auto px-4 pb-4 sm:px-8 lg:px-16"
      >
        <AnimatePresence mode="popLayout">
          {visible.map((p) => (
            <PropertyCard
              key={p.id}
              property={p}
              onClick={() => navigate(`/biens/${p.id}`)}
            />
          ))}
        </AnimatePresence>
        {visible.length === 0 && (
          <p className="py-16 font-serif text-xl italic text-slate-400">
            Aucun bien ne correspond à ce critère pour le moment.
          </p>
        )}
      </div>

      <div className="mx-auto mt-8 max-w-7xl px-4 sm:px-8 lg:px-16">
        <Link
          to="/biens"
          data-testid="properties-view-all"
          className="group inline-flex items-center gap-2 font-serif text-xl text-azur transition-colors hover:text-terracotta"
        >
          Voir tous les biens
          <ArrowRight size={20} className="transition-transform duration-300 group-hover:translate-x-1.5" />
        </Link>
      </div>
    </section>
  );
}
